/**
 * MODAL COMPONENT - Apple Liquid Glass Design System
 * macOS Tahoe 26.2 & iOS 26.2
 * Based on WWDC25: Meet Liquid Glass
 *
 * Features:
 * - Liquid Glass Thick material (deeper shadows, more lensing)
 * - "Bubble pop" morphing animation - expands from center
 * - Backdrop blur with animated dimming layer
 * - Fluid spring animations with gel-like physics
 * - Pearlescent shimmer on header
 * - Specular highlights with edge glow
 * - Parallax-like content entrance
 */

import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'
import { useEffect, useCallback } from 'react'
import { cn } from '../../lib/utils'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  children: React.ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  showCloseButton?: boolean
  closeOnBackdrop?: boolean
  closeOnEscape?: boolean
  footer?: React.ReactNode
}

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
  closeOnBackdrop = true,
  closeOnEscape = true,
  footer
}: ModalProps) {
  // Prevent scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  // Close on Escape key
  const handleEscape = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && isOpen && closeOnEscape) {
      onClose()
    }
  }, [isOpen, onClose, closeOnEscape])

  useEffect(() => {
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [handleEscape])

  // Handle backdrop click
  const handleBackdropClick = () => {
    if (closeOnBackdrop) {
      onClose()
    }
  }

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl',
    full: 'max-w-[95vw] max-h-[95vh]'
  }

  // Liquid Glass spring animation config - gel-like physics
  const springConfig = {
    type: "spring",
    damping: 28,
    stiffness: 350,
    mass: 0.9
  }

  // Content stagger animation
  const contentVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { delay: 0.1, duration: 0.3, ease: [0.16, 1, 0.3, 1] }
    }
  }

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <>
          {/* Dimming Backdrop with Glass Blur - animated blur intensity */}
          <motion.div
            initial={{ opacity: 0, backdropFilter: 'blur(0px)' }}
            animate={{ opacity: 1, backdropFilter: 'blur(20px)' }}
            exit={{ opacity: 0, backdropFilter: 'blur(0px)' }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-40 liquid-modal-backdrop"
            onClick={handleBackdropClick}
            aria-hidden="true"
          />

          {/* Modal Container */}
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? "modal-title" : undefined}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.85, y: 30, rotateX: -5 }}
              animate={{ opacity: 1, scale: 1, y: 0, rotateX: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20, rotateX: 5 }}
              transition={springConfig}
              className={cn(
                'w-full',
                sizeClasses[size],
                // Liquid Glass Thick material
                'liquid-material-thick liquid-lens',
                'rounded-3xl overflow-hidden',
                // Enhanced shadow for modals
                'shadow-liquid-xl dark:shadow-dark-liquid-xl',
                // Subtle border for depth
                'border border-white/10 dark:border-white/5'
              )}
              style={{ perspective: '1000px' }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              {(title || showCloseButton) && (
                <div className="relative flex items-center justify-between px-6 py-5 border-b border-light-separator dark:border-dark-separator">
                  {/* Specular highlight gradient */}
                  <div className="absolute inset-0 bg-gradient-to-b from-white/40 to-transparent dark:from-white/10 dark:to-transparent pointer-events-none" />

                  {title && (
                    <h2
                      id="modal-title"
                      className="relative text-title3 font-bold text-light-text-primary dark:text-dark-text-primary tracking-tight"
                    >
                      {title}
                    </h2>
                  )}

                  {showCloseButton && (
                    <motion.button
                      onClick={onClose}
                      className={cn(
                        'relative w-9 h-9 flex items-center justify-center rounded-xl',
                        'liquid-material-clear',
                        'hover:liquid-material-regular hover:shadow-glass-sm',
                        'group overflow-hidden',
                        !title && 'ml-auto'
                      )}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      transition={{ type: "spring", damping: 20, stiffness: 400 }}
                      aria-label="Close modal"
                    >
                      <X
                        className="w-5 h-5 text-light-text-tertiary dark:text-dark-text-tertiary group-hover:text-light-text-primary dark:group-hover:text-dark-text-primary transition-colors duration-fast relative z-10"
                        strokeWidth={2.5}
                      />
                      {/* Energize glow on hover */}
                      <span className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-base bg-gradient-radial from-white/20 to-transparent" />
                    </motion.button>
                  )}
                </div>
              )}

              {/* Content with stagger animation */}
              <motion.div
                className={cn(
                  'p-6 max-h-[calc(100vh-200px)] overflow-y-auto scrollbar-glass',
                  !title && !showCloseButton && 'pt-6'
                )}
                variants={contentVariants}
                initial="hidden"
                animate="visible"
              >
                {children}
              </motion.div>

              {/* Footer */}
              {footer && (
                <div className="relative px-6 py-4 border-t border-light-separator dark:border-dark-separator">
                  {/* Bottom specular hint */}
                  <div className="absolute inset-0 bg-gradient-to-t from-white/20 to-transparent dark:from-white/5 dark:to-transparent pointer-events-none" />
                  <div className="relative flex items-center justify-end gap-3">
                    {footer}
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  )
}

/**
 * BottomSheet - iOS-style bottom sheet with Liquid Glass
 */
interface BottomSheetProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  children: React.ReactNode
  showHandle?: boolean
}

export function BottomSheet({
  isOpen,
  onClose,
  title,
  children,
  showHandle = true
}: BottomSheetProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 liquid-modal-backdrop"
            onClick={onClose}
          />

          {/* Sheet */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{
              type: "spring",
              damping: 30,
              stiffness: 300
            }}
            className={cn(
              'fixed bottom-0 left-0 right-0 z-50',
              'liquid-material-thick liquid-lens',
              'rounded-t-3xl',
              'max-h-[90vh]',
              'shadow-liquid-xl dark:shadow-dark-liquid-xl'
            )}
          >
            {/* Handle */}
            {showHandle && (
              <div className="flex justify-center py-3">
                <div className="w-10 h-1 rounded-full bg-light-text-quaternary dark:bg-dark-text-quaternary" />
              </div>
            )}

            {/* Header */}
            {title && (
              <div className="px-6 pb-4 border-b border-light-separator dark:border-dark-separator">
                <h2 className="text-title3 font-bold text-light-text-primary dark:text-dark-text-primary tracking-tight text-center">
                  {title}
                </h2>
              </div>
            )}

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)] scrollbar-glass">
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

/**
 * Popover - Small floating glass panel
 */
interface PopoverProps {
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
  anchor?: { x: number; y: number }
  className?: string
}

export function Popover({
  isOpen,
  onClose,
  children,
  anchor,
  className
}: PopoverProps) {
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (isOpen) {
        onClose()
      }
    }
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [isOpen, onClose])

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: -10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: -10 }}
          transition={{
            type: "spring",
            damping: 20,
            stiffness: 300
          }}
          className={cn(
            'liquid-dropdown',
            className
          )}
          style={anchor ? { position: 'fixed', left: anchor.x, top: anchor.y } : undefined}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  )
}
